<template>
  <div class="meta-list">
    <NuxtLink
      v-if="state.articleType?.title"
      :to="state.articleType?.slug"
      class="meta-list-link is--secondary"
      @click="handleClick(state.articleType?.title, state.articleType?.slug)"
      @click.middle="handleClick(state.articleType?.title, state.articleType?.slug)"
      >{{ state.articleType?.title }}
    </NuxtLink>
    <time v-if="state.publishTime">{{ state.publishTime }}</time>
    <span v-if="state.adjustTime" class="meta-list-icon-item">
      <Icon
        name="eenvandaag-general:clock-rotate-right"
        class="meta-list-icon"
        role="presentation"
      />
      <time>{{ state.adjustTime }}</time>
    </span>
    <div v-for="author in state.authors" :key="author?.title">
      <NuxtLink
        v-if="author.showDetail"
        :to="`/biografieen/${author?.slug}`"
        class="meta-list-link is--primary meta-list-icon-item"
        @click="handleClick(author?.title, `/biografieen/${author?.slug}`)"
        @click.middle="handleClick(author?.title, `/biografieen/${author?.slug}`)"
      >
        <Icon name="eenvandaag-general:circle-user" class="meta-list-icon" role="presentation" />
        {{ author.title }}
      </NuxtLink>
      <span v-else class="meta-list-link is--primary meta-list-icon-item disabled">
        <Icon name="eenvandaag-general:circle-user" class="meta-list-icon" role="presentation" />
        {{ author.title }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import formatUtils from '~/utils/formatUtils';
import type { ICmsArticlesAuthors } from '~~/types/_cms-types';
import type { IAnalytics } from '~~/types/local_types';

interface IProps {
  authors?: ICmsArticlesAuthors[];
  publishTime?: string;
  adjustTime?: string;
  articleType?: string;
  analytics?: IAnalytics;
}

const props = defineProps<IProps>();

const { formatDate } = formatUtils();

const state = reactive({
  articleType: useArticleType(props.articleType),
  publishTime: props.publishTime && formatDate(props.publishTime, true, true),
  adjustTime: props.adjustTime && formatDate(props.adjustTime, true, true),
  authors: props.authors?.map((author) => author.authorsId!) || [],
});

const handleClick = (linkText: string | undefined, linkUrl: string | undefined) => {
  gtmTrackEvent({
    ...props.analytics,
    linkText,
    linkUrl,
  });
};
</script>

<style lang="postcss" scoped>
.meta-list {
  @add-mixin label-lg;

  --meta-list-gap: var(--unit-4) var(--unit-6);

  display: flex;
  align-items: center;
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--meta-list-gap);
  color: hsl(var(--color-primary-600));

  @media (--lg-viewport) {
    --meta-list-gap: var(--unit-6) var(--unit-8);
  }
}

.meta-list-link {
  font-weight: 600;
  text-decoration: underline;
  color: var(--meta-list-link-color);

  &.is--primary {
    --meta-list-link-color: hsl(var(--color-primary-700));
    --meta-list-link-hover-color: hsl(var(--color-primary-900));
  }

  &.is--secondary {
    --meta-list-link-color: hsl(var(--color-secondary-500));
    --meta-list-link-hover-color: hsl(var(--color-secondary-700));
  }

  &.disabled {
    text-decoration: none;

    &:hover {
      color: var(--meta-list-link-color);
    }
  }

  @media (--lg-viewport) {
    @media (hover: hover) and (pointer: fine) {
      /* when hover is supported and using a mouse */
      &:hover {
        color: var(--meta-list-link-hover-color);
      }
    }
  }
}

.meta-list-icon {
  --icon-size: var(--unit-16);

  font-size: var(--icon-size);

  @media (--lg-viewport) {
    --icon-size: var(--unit-20);
  }
}

.meta-list-icon-item {
  display: flex;
  align-items: center;
  gap: var(--unit-4);
}
</style>
